import _ from 'lodash';
import React from 'react';
import LoginRequired from 'components/LoginRequired';
import {connect} from 'react-redux';
import uuid from 'uuid/v4';

import {BOOKING_DETAIL_URL, ROOM_PURPOSES_URL} from 'config/urls';

import {reverse} from 'utils/urls';

import Page from 'components/Page';
import ListContent from 'components/ListContent';

import {ConfirmationModal} from 'components/CommonModals';
import Loading from 'components/Loading';
import {withFilters, FILTERS} from 'components/withFilters';
import RoomNotesModal from 'components/RoomNotesModal';
import VisibleIf from 'components/VisibleIf';

import {
  MODALS,
  fetchAllocations,
  selectAllocations,
  selectAllocationsLoading,
  fetchAllocationStatistics,
  selectAllocationStatistics,
  selectTimelineData,
  initialFilters,
  deleteOfflinePeriod,
  changeCleaningStatus,
  fetchOfflinePeriodReasons,
  selectOfflinePeriodReasons
} from 'ducks/allocations';
import {
  fetchRoomTypes,
  selectRoomTypes,
  fetchFloors,
  selectFloors,
  fetchAllRooms,
  selectAllRooms,
  fetchRoomTypeFilters,
  selectRoomTypeFilters
} from 'ducks/rooms';
import {
  MODALS as BASE_MODALS,
  openModal,
  isModalOpen,
  closeModal,
  storeDataInModal
} from 'ducks/modals';

import {composeActions} from 'ducks/common';

import {
  AllocationsCalendar,
  AllocationsFilters,
  CreateRoomModal,
  ArchiveRoomModal,
  CreateOfflinePeriodModal,
  OfflinePeriodDetailModal,
  SubNavigation,
  OutOfServiceReasonModal,
  UndoArchiveRoomModal
} from './components';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

class RoomAllocations extends React.Component<any> {
  state = {
    statisticsBoxOpen: false
  };

  componentDidMount() {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_ALLOCATIONS, locationRef},
      () => {
        this.props.fetchAllocations({locationRef});
        this.props.fetchAllocationStatistics({locationRef});
        this.props.fetchRoomTypes({locationRef});
        this.props.fetchAllRooms({locationRef});
        this.props.fetchFloors({locationRef});
        this.props.fetchRoomTypeFilters({locationRef});
        this.props.fetchOfflinePeriodReasons();
      }
    );
  }

  timelineDoubleClickHandler = itemId => {
    const {allocations, match: {params: {locationRef}}} = this.props;

    const allocation = _.find(allocations, allocation =>
      allocation.statuses.some(status => status.id === itemId)
    );
    const status = _.find(allocation.statuses, status => status.id === itemId);

    const room = {
      roomNumber: allocation.roomNumber,
      roomId: allocation.roomId
    };

    // Offline period
    if (!_.isNil(status.offlinePeriodId)) {
      return this.openOfflinePeriodDetailModal({
        offlinePeriodId: status.offlinePeriodId,
        room
      });
    }

    // Available
    if (_.isNil(status.bookingId)) {
      return this.openCreateOfflinePeriodModal({room});
    }

    // Reserved / Occupied
    if (!_.isNil(status.bookingId)) {
      const url = reverse(BOOKING_DETAIL_URL, {
        locationRef,
        bookingId: status.bookingId
      });
      window.open(url);
    }
  };

  openCreateRoomModal = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.CREATE_ROOM
      },
      () => this.props.openModal(MODALS.CREATE_ROOM, {locationRef})
    );
  };

  openRoomNotesModal = ({roomId}) => {
    const {locationRef} = this.props.match.params;
    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_ROOM_NOTES
      },
      () =>
        this.props.openModal(BASE_MODALS.ROOM_NOTES, {
          roomId,
          locationRef,
          refetchExtraAction: () => []
        })
    );
  };

  openCreateOfflinePeriodModal = ({room}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD
      },
      () =>
        this.props.openModal(MODALS.CREATE_OFFLINE_PERIOD, {locationRef, room})
    );
  };

  openConfirmationModal = ({heading, confirmAction, question, ...args}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction,
      ...args
    });

  openOfflinePeriodDetailModal = ({offlinePeriodId, room}) => {
    const {locationRef} = this.props.match.params;

    this.props.openModal(MODALS.OFFLINE_PERIOD_DETAIL, {
      locationRef,
      offlinePeriodId,
      room
    });
  };

  deleteOfflinePeriod = ({offlinePeriodId}) => {
    const {locationRef} = this.props.match.params;

    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => closeModal(MODALS.OFFLINE_PERIOD_DETAIL),
      () => fetchAllocations({locationRef}),
      () => fetchAllocationStatistics({locationRef})
    ];

    const failureExtraActions = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD
      },
      () =>
        this.props.deleteOfflinePeriod(
          {offlinePeriodId, locationRef},
          successExtraActions,
          failureExtraActions
        )
    );
  };

  openDeleteOfflinePeriodModal = ({offlinePeriodId, roomNumber}) => {
    this.openConfirmationModal({
      heading: `Delete Out Of Order Period`,
      question: `Are you sure you want to delete out of order period for room ${roomNumber}?`,
      confirmAction: () => this.deleteOfflinePeriod({offlinePeriodId})
    });
  };

  calculateAllocations = () => {
    const {allocationStatistics} = this.props;
    const [dummyRooms, regularRooms] = _.partition(
      allocationStatistics,
      room => room.dummy
    ).map(rooms => rooms.length);

    return {
      regularRooms,
      dummyRooms
    };
  };

  prepareStatisticBoxInfo = () => {
    const {roomTypeGroups, allocationStatistics} = this.props;
    const filters = this.props.getFilters();

    const roomTypesFilter = _.split(filters.roomTypes, ',');

    let filteredRoomTypeGroups = roomTypeGroups.filter(roomType =>
      _.includes(roomTypesFilter, roomType.ref)
    );

    if (filteredRoomTypeGroups.length === 0) {
      filteredRoomTypeGroups = roomTypeGroups;
    }

    const groupedRooms = _.groupBy(allocationStatistics, 'roomType.ref');
    let roomTypes: any = [];
    _.map(filteredRoomTypeGroups, groups => {
      roomTypes = [...roomTypes, ...groups.roomTypes];
    });

    const mappedRoomTypes = _.map(roomTypes, roomType => {
      const roomTypeAllocations = _.get(groupedRooms, roomType.ref, []);
      const roomTypeDummyAllocations = roomTypeAllocations.filter(
        ({dummy}) => dummy
      );

      return {
        label: roomType.name,
        totalReal: roomType.roomCount - roomType.dummyRoomCount,
        currentlySelected:
          roomTypeAllocations.length - roomTypeDummyAllocations.length,
        totalDummy: roomType.dummyRoomCount,
        currentlySelectedDummy: roomTypeDummyAllocations.length,
        key: uuid()
      };
    });
    return mappedRoomTypes;
  };

  toggleStatisticsBox = () => {
    this.setState({statisticsBoxOpen: !this.state.statisticsBoxOpen});
  };

  closeStatisticsBox = () => {
    this.setState({statisticsBoxOpen: false});
  };

  setFilter = (field, value, withoutFetching = false) => {
    const {locationRef} = this.props.match.params;

    if (!withoutFetching) {
      this.props.composeActions([
        () => this.props.setFilter(field, value),
        () => fetchAllocations({locationRef}),
        () => fetchAllocationStatistics({locationRef})
      ]);
    } else {
      this.props.setFilter(field, value);
    }
  };

  // When clearing the status, clear also the selected dates for this status
  clearStatus = () => {
    const {locationRef} = this.props.match.params;

    this.props.composeActions([
      () => this.props.setFilter('status', null),
      () => this.props.setFilter('startDate', null),
      () => this.props.setFilter('endDate', null),
      () => fetchAllocations({locationRef}),
      () => fetchAllocationStatistics({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => fetchAllocations({locationRef}),
      () => fetchAllocationStatistics({locationRef})
    ]);
  };

  openOutOfServiceReasonModal = ({roomId, cleaningStatus}) => {
    const {locationRef} = this.props.match.params;
    this.props.openModal(MODALS.OUT_OF_SERVICE_REASON, {
      roomId,
      locationRef,
      cleaningStatus
    });
  };

  redirectToRoomPurposes = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_ROOM_PURPOSES, locationRef},
      () => {
        this.props.history.push(
          reverse(ROOM_PURPOSES_URL, {
            locationRef
          })
        );
      }
    );
  };
  subNavigationWithData = props => (
    <SubNavigation
      statistics={this.calculateAllocations()}
      toggleStatisticsBox={this.toggleStatisticsBox}
      redirectToRoomPurposes={this.redirectToRoomPurposes}
      {...props}
    />
  );

  render() {
    const {
      rooms,
      floors,
      timelineData,
      createRoomModal,
      archiveRoomModal,
      confirmationModal,
      roomNotesModal,
      isLoading,
      createOfflinePeriodModal,
      offlinePeriodDetailModal,
      outOfServiceReasonModal,
      roomTypeFilters,
      outOfOrderFilters,
      undoArchiveRoomModal
    } = this.props;

    const {locationRef} = this.props.match.params;

    const filters = this.props.getFilters();
    const filterStartDate = _.get(filters, 'startDate.value');
    const filterEndDate = _.get(filters, 'endDate.value');
    const stats = this.prepareStatisticBoxInfo();

    return (
      <Page activeTab="allocations" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_ALLOCATIONS}
          locationRef={locationRef}>
          <ListContent
            className="RoomAllocations"
            filters={filters}
            initialFilters={initialFilters}
            setFilter={this.setFilter}
            navigationExtraElement={this.subNavigationWithData}>
            <ListContent.Content>
              <AllocationsCalendar
                locationRef={locationRef}
                timeline={timelineData}
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
                doubleClickHandler={this.timelineDoubleClickHandler}
                openRoomNotesModal={this.openRoomNotesModal}
                statisticsBoxInfo={stats}
                closeStatisticsBox={this.closeStatisticsBox}
                statisticsBoxOpen={this.state.statisticsBoxOpen}
                changeCleaningStatus={this.props.changeCleaningStatus}
                openOutOfServiceReasonModal={this.openOutOfServiceReasonModal}
              />

              {isLoading && (
                <div className="loadingContainer">
                  <Loading />
                </div>
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <AllocationsFilters
                locationRef={locationRef}
                rooms={rooms}
                clearStatus={this.clearStatus}
                floors={floors}
                roomTypeFilters={roomTypeFilters}
                filters={filters}
                setFilter={this.setFilter}
                clearFilters={this.clearFilters}
                outOfOrderFilters={outOfOrderFilters}
              />
            </ListContent.Filters>
          </ListContent>

          {createRoomModal && <CreateRoomModal />}
          {archiveRoomModal && <ArchiveRoomModal />}
          {confirmationModal && <ConfirmationModal />}
          {roomNotesModal && <RoomNotesModal />}
          {createOfflinePeriodModal && <CreateOfflinePeriodModal />}
          {offlinePeriodDetailModal && (
            <OfflinePeriodDetailModal
              openDeleteOfflinePeriodModal={this.openDeleteOfflinePeriodModal}
            />
          )}
          {outOfServiceReasonModal && <OutOfServiceReasonModal />}
          {undoArchiveRoomModal && <UndoArchiveRoomModal />}
        </VisibleIf>
      </Page>
    );
  }
}

const mapStateToProps = state => {
  return {
    timelineData: selectTimelineData(state),
    roomTypeGroups: selectRoomTypes(state),
    rooms: selectAllRooms(state),
    allocations: selectAllocations(state),
    floors: selectFloors(state),
    allocationStatistics: selectAllocationStatistics(state),
    isLoading: selectAllocationsLoading(state),
    createRoomModal: isModalOpen(state, MODALS.CREATE_ROOM),
    archiveRoomModal: isModalOpen(state, MODALS.ARCHIVE_ROOM),
    confirmationModal: isModalOpen(state, BASE_MODALS.CONFIRM),
    roomNotesModal: isModalOpen(state, BASE_MODALS.ROOM_NOTES),
    roomTypeFilters: selectRoomTypeFilters(state),
    outOfOrderFilters: selectOfflinePeriodReasons(state),
    createOfflinePeriodModal: isModalOpen(state, MODALS.CREATE_OFFLINE_PERIOD),
    offlinePeriodDetailModal: isModalOpen(state, MODALS.OFFLINE_PERIOD_DETAIL),
    outOfServiceReasonModal: isModalOpen(state, MODALS.OUT_OF_SERVICE_REASON),
    undoArchiveRoomModal: isModalOpen(state, MODALS.UNDO_ARCHIVE_ROOM)
  };
};

const mapDispatchToProps = {
  fetchAllocations,
  fetchAllocationStatistics,
  fetchRoomTypes,
  fetchFloors,
  openModal,
  deleteOfflinePeriod,
  requiresPermission,
  fetchAllRooms,
  fetchRoomTypeFilters,
  composeActions,
  changeCleaningStatus,
  fetchOfflinePeriodReasons
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withFilters(LoginRequired(RoomAllocations), FILTERS.ALLOCATIONS_CALENDAR, {
    initialFilters
  })
);
